// The signed-in user's connected mailboxes: what the owner may read of each
// (server, user name, kind of sign-in, health), and the actions the mail edge
// function takes for them (test, connect, check, disconnect). A password goes
// to the server once, when connecting, and never comes back. An Outlook.com
// mailbox is connected through Microsoft's sign-in instead.

import type { MailConnection } from '@cerebro-diem/core';
import { supabase } from './supabase';
import { invokeEdgeFunction } from './edgeFunctions';
import { isRecord } from './errors';

export type { MailConnection };

export interface MailFolder {
  name: string;
  role: 'inbox' | 'junk' | 'sent' | 'trash' | 'archive' | 'drafts' | null;
}

const ROLE_WORDS: Record<NonNullable<MailFolder['role']>, string> = {
  inbox: 'Inbox',
  junk: 'Junk',
  sent: 'Sent',
  trash: 'Trash',
  archive: 'Archive',
  drafts: 'Drafts',
};

/** The folders a sign-in found, as one line: the ones with a known purpose named, the rest counted. */
export function foldersSummary(folders: MailFolder[]): string {
  const named = folders.filter((f) => f.role !== null).map((f) => ROLE_WORDS[f.role as NonNullable<MailFolder['role']>]);
  const others = folders.length - named.length;
  const parts = [...new Set(named)];
  if (others > 0) parts.push(`${others} other folder${others === 1 ? '' : 's'}`);
  return parts.length ? `Signed in. Folders: ${parts.join(', ')}.` : 'Signed in. The mailbox has no folders yet.';
}

export interface MailboxInput {
  host: string;
  username: string;
  password: string;
}

export const mailKeys = {
  connections: ['mail', 'connections'] as const,
};

export async function mailConnections(): Promise<MailConnection[]> {
  const { data, error } = await supabase
    .from('mail_connections')
    .select('id, host, username, auth, needs_reconnect, last_error, connected_at, last_checked_at, last_sync_at, sync_backlog')
    .order('username');
  if (error) throw error;
  return (data ?? []) as MailConnection[];
}

function folders(result: unknown, what: string): MailFolder[] {
  if (!isRecord(result) || !Array.isArray(result.folders)) throw new Error(`The server answered the ${what} with something unexpected`);
  return result.folders as MailFolder[];
}

/** Sign in to the mailbox and list its folders without keeping anything. */
export async function testMailbox(input: MailboxInput): Promise<MailFolder[]> {
  return folders(await invokeEdgeFunction('mail', { action: 'test', ...input }), 'test');
}

/** Sign in, and when that works keep the mailbox and its password. The same mailbox again replaces the password. */
export async function connectMailbox(input: MailboxInput): Promise<{ id: string; folders: MailFolder[] }> {
  const result = await invokeEdgeFunction('mail', { action: 'connect', ...input });
  if (!isRecord(result) || typeof result.id !== 'string') throw new Error('The server answered the connection with something unexpected');
  return { id: result.id, folders: folders(result, 'connection') };
}

/** Sign in to a saved mailbox with its saved password or sign-in. */
export async function checkMailbox(id: string): Promise<MailFolder[]> {
  return folders(await invokeEdgeFunction('mail', { action: 'check', id }), 'check');
}

/** Read a saved mailbox now, as the background reading does every five minutes. */
export async function syncMailbox(id: string): Promise<{ messages: number; backlog: boolean }> {
  const result = await invokeEdgeFunction('mail', { action: 'sync', id });
  if (!isRecord(result) || typeof result.messages !== 'number' || typeof result.backlog !== 'boolean') {
    throw new Error('The server answered the read with something unexpected');
  }
  return { messages: result.messages, backlog: result.backlog };
}

export async function disconnectMailbox(id: string): Promise<void> {
  await invokeEdgeFunction('mail', { action: 'disconnect', id });
}

const MICROSOFT_SIGN_IN = 'https://login.microsoftonline.com/';

/** Ask the server for Microsoft's sign-in page for a mailbox and go there; Microsoft sends the user back to Integrations. */
export async function startOutlookMailboxConnect(): Promise<void> {
  const result = await invokeEdgeFunction('microsoft', { action: 'oauth_start', purpose: 'mail' });
  const url = isRecord(result) ? result.url : null;
  if (typeof url !== 'string' || !url.startsWith(MICROSOFT_SIGN_IN)) {
    throw new Error('The server did not return a Microsoft sign-in address');
  }
  window.location.assign(url);
}
